import { AppDispatch } from '../../../redux/store';
import { createDealFileThunk } from '../../../redux/thunks/deal';
import CustomInput from '../../reusable/input';
import CustomModal from '../../modals';
import { gray } from '../../../theme/themePrimitives';
import LabelWrapper from '../../wrapper/label';
import { useDispatch } from 'react-redux';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { DeleteOutlineOutlined, UploadFile } from '@mui/icons-material';
import { useRef, useState } from 'react';

/**
 * @heading1: TransferDealModal
 * @paragraph:A modal component for transferring a deal to another stage or pipeline.
 * Users can select a pipeline and stage to transfer the deal.
 * @param: {boolean} open - Determines whether the modal is open.
 * @param: {function} onClose - Function to handle closing the modal.
 */

const AddDocumentModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const dispatch = useDispatch<AppDispatch>();
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    // Optional: store Base64 string if you need to upload it
    const [fileBase64, setFileBase64] = useState<string | null>(null);
    // Store the "display name" the user can edit
    const [fileName, setFileName] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const fileInputRef = useRef<HTMLInputElement | null>(null);

    // useEffect(() => {
    //     setSelectedFile(null);
    //     setFileBase64(null);
    //     setFileName('');
    //     setIsLoading(false);
    // }, []);

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    // Convert file to Base64 (if needed) and store in state
    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (loadEvent) => {
            const base64String = loadEvent.target?.result as string;
            setFileBase64(base64String);
            setSelectedFile(file);
            // Set file name without extension (optional)
            const baseName = file.name.replace(/\.[^/.]+$/u, '');
            setFileName(baseName);
        };
        reader.readAsDataURL(file);
    };
    // Remove file from state
    const handleRemoveFile = () => {
        setSelectedFile(null);
        setFileBase64(null);
        setFileName('');
        // Clear the input value to allow re-upload of the same file
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };
    const handleUpload = async () => {
        if (selectedFile && fileBase64) {
            setIsLoading(true);
            try {
                await dispatch(createDealFileThunk({
                    file: fileBase64,
                    file_name: fileName
                })).unwrap();
                handleRemoveFile();
                onClose();
            } catch (e) {
                //
            } finally {
                setIsLoading(false);
            }
        }
        // Close the modal if desired
    };

    const onCloseHandler = () => {
        handleRemoveFile();
        onClose();
    };

    const fileSizeInKB = selectedFile ? Math.round(selectedFile.size / 1024) : 0;
    return <CustomModal open={open} handleClose={isLoading ? () => null : onCloseHandler} title="Upload Document">
        {fileBase64
            ? <Box display="flex" flexDirection="column" gap={2}>
                {/* Filename text field */}
                <CustomInput
                    label="Filename"
                    variant="outlined"
                    fullWidth
                    value={fileName}
                    disabled={isLoading}
                    onChange={(e) => setFileName(e.target.value)}
                />
                <Box><LabelWrapper label="Uploaded File" />

                    {/* File details (icon, name, size, delete) */}
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gap={2}
                        p={2}
                        border="1px solid #ccc"
                        borderRadius={1}
                    >
                        {/* Replace this with your own file icon or preview if desired */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box>
                                <Typography variant="body2" fontWeight="bold">
                                    {selectedFile?.name}
                                </Typography>
                                <Typography variant="caption">{fileSizeInKB} KB</Typography>
                            </Box>
                        </Box>

                        <IconButton color="error" onClick={handleRemoveFile} disabled={isLoading}>
                            <DeleteOutlineOutlined />
                        </IconButton>
                    </Box>
                </Box>
                <Box display="flex" gap={1}>
                    <Button variant="outlined" color="secondary" fullWidth onClick={onCloseHandler} disabled={isLoading}>Cancel</Button>
                    <Button variant="contained" fullWidth onClick={handleUpload} disabled={!fileName.trim() || isLoading}>{isLoading ? 'Uploading...' : 'Upload'}</Button>
                </Box>
            </Box>
            : <Box>
                <Box
                    onClick={handleClick}
                    border="2px dashed #ccc"
                    borderRadius={2}
                    px={3}
                    py={5}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    gap={1}
                    sx={{
                        'cursor': 'pointer',
                        'transition': '0.2s',
                        '&:hover': {
                            backgroundColor: '#f9f9f9'
                        }
                    }}
                >
                    <Box mb={1} p={1} borderRadius={1} color={gray[500]} bgcolor={gray[200]}><UploadFile /></Box>
                    <Typography variant="body1">Click here to upload</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Max file size: 50 MB (CSV, DOC, PPT)
                    </Typography>
                </Box>

                {/* Hidden file input */}
                <input
                    ref={fileInputRef}
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    disabled={isLoading}
                />
                {fileBase64 && (
                    <Box mt={2}>
                        <Typography variant="body2" sx={{ wordWrap: 'break-word' }}>
                            <strong>Base64 Data:</strong> {fileBase64}
                        </Typography>
                    </Box>
                )}
            </Box>}
    </CustomModal>;
};

export default AddDocumentModal;